import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { CodeBlock } from '@/components/ui/code-block'
import { ArrowRight, AlertTriangle, Info } from 'lucide-react'

const quickstartCode = `from zone_ambient_sdk import (
    AmbientEngine, AgenticProvider,
    ToolRegistry, PolicySpec, StreamEvent
)
from zone_ambient_sdk.tools.executors import setup_default_tools

# Setup
policy = PolicySpec(policy_text="Alert on payment failures >= $100")
provider = AgenticProvider()
registry = ToolRegistry()
setup_default_tools(registry)

# Create engine
engine = AmbientEngine(policy, provider, registry)

# Process events
event = StreamEvent(event_type="payment", text="Payment failed $499 ACME VIP")
result = engine.process_event(event)
print(result.decision.decision_type)  # ACT / IGNORE / DIGEST`

const useCases = [
  { name: 'Ops Feed Guardian', status: 'demo', description: 'Monitor operational events and alert on critical issues' },
  { name: 'Support Escalation Sentinel', status: 'coming', description: 'Escalate urgent support tickets automatically' },
  { name: 'Stripe / Payments Guardian', status: 'coming', description: 'Watch payment failures and VIP customer issues' },
  { name: 'CI Health Shepherd', status: 'coming', description: 'Track build failures and deployment issues' },
  { name: 'Security Anomaly Watcher', status: 'coming', description: 'Detect suspicious login patterns and access attempts' },
  { name: 'Invoice / Renewal Sentinel', status: 'coming', description: 'Track failed renewals and billing issues' },
]

export default function Landing() {
  return (
    <div className="container py-12">
      {/* PoC Disclaimer Banner */}
      <Alert className="mb-8 border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Proof of Concept</AlertTitle>
        <AlertDescription className="text-blue-700">
          This is a demonstration SDK showcasing ambient agent capabilities. Full production library coming soon.
        </AlertDescription>
      </Alert>

      {/* Hero */}
      <section className="text-center py-16">
        <h1 className="font-display text-5xl md:text-6xl mb-4">
          Zone <span className="text-accent">Ambient</span> SDK
        </h1>
        <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
          Ambient agents for streaming systems — act rarely, act correctly.
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Button asChild size="lg">
            <a href="/docs">Read Docs</a>
          </Button>
          <Button asChild variant="accent" size="lg">
            <Link to="/demo/config">
              Try Demo
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-4">
          Powered by{' '}
          <a
            href="https://theagentic.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            TheAgentic
          </a>
          's neuro-symbolic models for enhanced reasoning and tool-calling accuracy.
        </p>
      </section>

      {/* What is an Ambient Agent? */}
      <section className="py-12">
        <h2 className="font-display text-3xl text-center mb-8">What is an Ambient Agent?</h2>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2">Watches Constantly</h3>
              <p className="text-sm text-muted-foreground">
                Consumes a continuous stream of events — errors, payments, security alerts, metrics.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2">Acts Rarely</h3>
              <p className="text-sm text-muted-foreground">
                Only triggers when thresholds are met. No spam. Most events are ignored or added to digest.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2">Executes Real Actions</h3>
              <p className="text-sm text-muted-foreground">
                Sends email, WhatsApp, creates tickets — with guardrails, cooldowns, and audit trail.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quickstart */}
      <section className="py-12">
        <h2 className="font-display text-3xl text-center mb-8">Quickstart</h2>
        <div className="max-w-2xl mx-auto">
          <CodeBlock code={quickstartCode} language="python" />
          <p className="text-sm text-muted-foreground text-center mt-4">
            Demo ships with real email and WhatsApp executors.
          </p>
        </div>
      </section>

      {/* Why Zone Ambient SDK */}
      <section className="py-12">
        <h2 className="font-display text-3xl text-center mb-8">Why Zone Ambient SDK?</h2>
        <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
          <Badge variant="secondary" className="text-sm py-1 px-3">Natural-language behavior spec</Badge>
          <Badge variant="secondary" className="text-sm py-1 px-3">Strict tool-call schemas</Badge>
          <Badge variant="secondary" className="text-sm py-1 px-3">Cooldown + dedupe</Badge>
          <Badge variant="secondary" className="text-sm py-1 px-3">Audit logs & replay</Badge>
          <Badge variant="secondary" className="text-sm py-1 px-3">Dry run mode</Badge>
          <Badge variant="secondary" className="text-sm py-1 px-3">Budget limits</Badge>
        </div>
      </section>

      {/* Ambient Agents vs Generic Frameworks */}
      <section className="py-12 bg-muted/30 -mx-4 px-4 md:-mx-8 md:px-8">
        <h2 className="font-display text-3xl text-center mb-8">Not Another Agent Framework</h2>
        <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
          Ambient agents are fundamentally different from chatbot-style agent frameworks.
        </p>
        <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
          <Card className="border-accent/50">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-4 text-accent">Zone Ambient SDK</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>"Act rarely"</strong> — high precision, low spam</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>Stream-driven</strong> — continuous event consumption</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>Built-in guardrails</strong> — cooldowns, budgets, deduplication</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>Native audit trail</strong> — every decision logged</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>Spam prevention</strong> — core design principle</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-4 text-muted-foreground">Typical Agent Frameworks</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span>○</span>
                  <span>"Respond helpfully" — optimized for engagement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>○</span>
                  <span>Request-driven — user initiates interaction</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>○</span>
                  <span>Optional guardrails — often an afterthought</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>○</span>
                  <span>Logging varies — often incomplete</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>○</span>
                  <span>Spam prevention — bolted on later</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Demo Spotlight */}
      <section className="py-12">
        <h2 className="font-display text-3xl text-center mb-4">Demo: Ops Feed Guardian</h2>
        <p className="text-center text-muted-foreground mb-8">Try it in 60 seconds</p>
        <Card className="max-w-2xl mx-auto border-accent/50">
          <CardContent className="p-6">
            <ol className="list-decimal list-inside space-y-2 text-sm mb-6">
              <li>Configure policy + enable tools (email/WhatsApp)</li>
              <li>Send a test message to verify delivery</li>
              <li>Inject events using quick buttons</li>
              <li>Watch decisions and verify real alerts</li>
            </ol>
            <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-md text-yellow-800 text-sm">
              <AlertTriangle className="h-4 w-4" />
              <span>This demo can send real messages if enabled.</span>
            </div>
            <Button asChild className="w-full mt-4" variant="accent">
              <Link to="/demo/config">
                Start Demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Use Cases */}
      <section className="py-12">
        <h2 className="font-display text-3xl text-center mb-8">Use Cases</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {useCases.map((uc) => (
            <Card key={uc.name} className={uc.status === 'demo' ? 'border-accent/50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">{uc.name}</h3>
                  <Badge variant={uc.status === 'demo' ? 'act' : 'secondary'}>
                    {uc.status === 'demo' ? 'Demo' : 'Coming Soon'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{uc.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t mt-12">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <a href="/docs" className="hover:text-foreground">Docs</a>
          <Link to="/demo/config" className="hover:text-foreground">Demo</Link>
          <a
            href="https://theagentic.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground"
          >
            Powered by TheAgentic
          </a>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-4">
          Zone Ambient SDK is a library for building ambient behaviors — not a hosted service (yet).
        </p>
        <p className="text-center text-xs text-muted-foreground mt-2">
          Feedback: <a href="mailto:develop@trymyzone.com" className="hover:text-foreground">develop@trymyzone.com</a>
        </p>
      </footer>
    </div>
  )
}
